import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../supabaseClient';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiMapPin, FiCheckCircle, FiXCircle, FiGift, FiCoffee, FiShare2, FiPlus, FiUser } = FiIcons;

export default function PartyGuestView() {
  const { id } = useParams();
  const [party, setParty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guestName, setGuestName] = useState('');
  const [guestCount, setGuestCount] = useState(1);
  const [message, setMessage] = useState('');
  const [rsvpStatus, setRsvpStatus] = useState(null); 
  const [submitted, setSubmitted] = useState(false);
  
  // Buffet Logic
  const [buffetItems, setBuffetItems] = useState([]);
  const [newBuffetItem, setNewBuffetItem] = useState('');
  
  useEffect(() => {
    fetchParty();
    fetchBuffet();
  }, [id]);

  const fetchParty = async () => {
    try {
      const { data, error } = await supabase.from('parties').select('*').eq('id', id).single();
      if (error) throw error;
      setParty(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBuffet = async () => {
      const { data } = await supabase.from('party_items').select('*').eq('party_id', id).eq('type', 'buffet');
      setBuffetItems(data || []);
  };

  const handleRSVP = async (status) => {
    if (!guestName.trim()) return alert('Bitte Namen eingeben');
    try {
      const { error } = await supabase.from('party_guests').insert({
        party_id: id,
        name: guestName,
        count: guestCount,
        status: status,
        message: message
      });
      if (error) throw error;
      setRsvpStatus(status);
      setSubmitted(true);
    } catch (error) {
      alert('Fehler: ' + error.message);
    }
  };

  const handleShare = async () => {
      if (navigator.share) {
          try {
              await navigator.share({
                  title: party.title,
                  text: `Du bist eingeladen: ${party.title}`,
                  url: window.location.href
              });
          } catch (err) { console.log('Share canceled'); }
      } else {
          navigator.clipboard.writeText(window.location.href);
          alert('Link in die Zwischenablage kopiert!');
      }
  };

  const claimItem = async (itemId) => {
      if (!guestName) return alert('Bitte gib zuerst oben deinen Namen ein, damit wir wissen wer das mitbringt.');
      await supabase.from('party_items').update({ assigned_to: guestName }).eq('id', itemId);
      fetchBuffet();
  };

  const addOwnItem = async () => {
      if (!guestName) return alert('Bitte gib zuerst oben deinen Namen ein.');
      if (!newBuffetItem.trim()) return;
      await supabase.from('party_items').insert({
          party_id: id,
          type: 'buffet',
          name: newBuffetItem,
          is_request: false,
          assigned_to: guestName
      });
      setNewBuffetItem('');
      fetchBuffet();
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50">Laden...</div>;
  if (!party) return <div className="min-h-screen flex items-center justify-center bg-gray-50">Event nicht gefunden.</div>;

  const wishedItems = buffetItems.filter(i => i.is_request && !i.assigned_to);
  const broughtItems = buffetItems.filter(i => i.assigned_to);

  return (
    <div className="min-h-screen bg-gray-50 pb-20 font-sans">
      {/* Hero Image */}
      <div className="h-64 md:h-96 relative bg-gray-900">
        {party.image_url ? (
            <img src={party.image_url} className="w-full h-full object-cover opacity-90" alt="Cover" />
        ) : (
            <div className="w-full h-full bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center">
                <SafeIcon icon={FiGift} className="w-20 h-20 text-white opacity-50" />
            </div>
        )}
        <div className="absolute inset-0 bg-black bg-opacity-30 flex items-end p-6 justify-between">
            <h1 className="text-3xl md:text-5xl font-bold text-white shadow-sm">{party.title}</h1>
            <button onClick={handleShare} className="bg-white/20 hover:bg-white/30 text-white p-3 rounded-full backdrop-blur-sm transition-all">
                <SafeIcon icon={FiShare2} className="w-6 h-6" />
            </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 -mt-10 relative z-10 space-y-8">
        
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 space-y-8">
            {/* Key Facts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 p-3 rounded-full text-blue-600">
                        <SafeIcon icon={FiCalendar} className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Wann?</p>
                        <p className="font-semibold text-gray-900">{format(new Date(party.date), 'EEEE, d. MMMM', { locale: de })}</p>
                        <p className="text-gray-600">{format(new Date(party.date), 'HH:mm')} Uhr</p>
                    </div>
                </div>
                <div className="flex items-center space-x-4">
                    <div className="bg-red-100 p-3 rounded-full text-red-600">
                        <SafeIcon icon={FiMapPin} className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Wo?</p>
                        <a href={`https://maps.google.com/?q=${encodeURIComponent(party.location)}`} target="_blank" rel="noreferrer" className="font-semibold text-blue-600 hover:underline">
                            {party.location || 'Wird bekanntgegeben'}
                        </a>
                    </div>
                </div>
            </div>

            <div className="prose max-w-none text-gray-600 whitespace-pre-wrap">{party.description}</div>

            {/* RSVP Section */}
            {!submitted ? (
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 mt-8">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Bist du dabei?</h3>
                    <div className="space-y-4">
                        <input 
                            type="text" 
                            placeholder="Dein Name" 
                            className="w-full border border-gray-300 rounded-lg p-3"
                            value={guestName}
                            onChange={e => setGuestName(e.target.value)}
                        />
                        <div className="flex items-center space-x-4">
                            <label className="text-gray-700">Personen:</label>
                            <select 
                                value={guestCount} 
                                onChange={e => setGuestCount(parseInt(e.target.value))}
                                className="border border-gray-300 rounded-lg p-2 w-20"
                            >
                                {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </div>
                        <textarea 
                            placeholder="Nachricht (Optional)" 
                            className="w-full border border-gray-300 rounded-lg p-3"
                            rows="2"
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                        />
                        <div className="grid grid-cols-2 gap-4 pt-2">
                            <button onClick={() => handleRSVP('accepted')} className="bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 flex items-center justify-center">
                                <SafeIcon icon={FiCheckCircle} className="w-5 h-5 mr-2" /> Zusage
                            </button>
                            <button onClick={() => handleRSVP('declined')} className="bg-gray-200 text-gray-700 py-3 rounded-lg font-bold hover:bg-gray-300 flex items-center justify-center">
                                <SafeIcon icon={FiXCircle} className="w-5 h-5 mr-2" /> Absage
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-green-50 text-green-800 p-8 rounded-xl text-center border border-green-200 mt-8">
                    <SafeIcon icon={FiCheckCircle} className="w-16 h-16 mx-auto mb-4 text-green-600" />
                    <h3 className="text-2xl font-bold mb-2">Danke, {guestName}!</h3>
                    <p>Deine Rückmeldung wurde gespeichert.</p>
                </div>
            )}
        </div>

        {/* Buffet Section (Only if enabled and RSVP accepted or not yet submitted) */}
        {party.features?.buffet && (
            <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
                <div className="flex items-center mb-6">
                    <div className="bg-pink-100 p-2 rounded-lg text-pink-600 mr-3">
                        <SafeIcon icon={FiCoffee} className="w-6 h-6" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Das Buffet</h2>
                </div>

                <div className="space-y-8">
                    {/* Wünsche */}
                    {wishedItems.length > 0 && (
                        <div>
                            <h3 className="font-bold text-gray-700 mb-3">Wir benötigen noch:</h3>
                            <div className="grid gap-3">
                                {wishedItems.map(item => (
                                    <div key={item.id} className="flex justify-between items-center p-4 bg-yellow-50 border border-yellow-100 rounded-lg">
                                        <span className="font-medium text-gray-800">{item.name}</span>
                                        <button 
                                            onClick={() => claimItem(item.id)}
                                            className="px-3 py-1.5 bg-yellow-400 text-yellow-900 text-sm font-bold rounded-lg hover:bg-yellow-500 transition-colors"
                                        >
                                            Ich mache das!
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Mitbringsel */}
                    <div>
                        <h3 className="font-bold text-gray-700 mb-3">Das wird mitgebracht:</h3>
                        {broughtItems.length > 0 ? (
                            <div className="grid gap-3">
                                {broughtItems.map(item => (
                                    <div key={item.id} className="flex items-center p-3 bg-gray-50 border border-gray-100 rounded-lg">
                                        <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mr-3" />
                                        <div>
                                            <span className="font-medium text-gray-900">{item.name}</span>
                                            <span className="text-sm text-gray-500 ml-2">(von {item.assigned_to})</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 italic">Noch keine Beiträge.</p>
                        )}
                    </div>

                    {/* Eigenes hinzufügen */}
                    <div className="pt-4 border-t border-gray-100">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Ich bringe etwas anderes mit:</label>
                        <div className="flex gap-2">
                            <input 
                                type="text" 
                                value={newBuffetItem}
                                onChange={e => setNewBuffetItem(e.target.value)}
                                placeholder="z.B. Nudelsalat"
                                className="flex-1 border border-gray-300 rounded-lg p-2"
                            />
                            <button 
                                onClick={addOwnItem}
                                className="bg-pink-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-pink-700"
                            >
                                Hinzufügen
                            </button>
                        </div>
                        {!guestName && <p className="text-xs text-red-500 mt-1">* Bitte gib oben erst deinen Namen ein.</p>}
                    </div>
                </div>
            </div>
        )}
      </div>
    </div>
  );
}